import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  DatePicker,
  Button,
  message,
  Modal,
  InputNumber,
  Select,
} from "antd";
import { EnvironmentOutlined, PhoneOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import config from "../../../config";
import SingleImageUploader from "../../shared/imageUploaders/singleImage/SingleImageUploader";
import axios from "axios";

// Components
import PoliticalParty from "./components/PoliticalParty";
import Religion from "./components/Religion";
import Position from "./components/Position";
import OfficeSelectionReformed from "./components/office/OfficeSelectionReformed";
import Vote from "./components/Vote";
import Contribution from "./components/Contribution";
import Committee from "./components/Committee";
import FamilyNew from "./components/FamilyNew";
import Education from "./components/Education";
import SocialMedia from "./components/SocialMedia";
import OfficeContacts from "./components/OfficeContacts";
import ProfessionalExperience from "./components/ProfessionalExperience";

// Google Place Autocomplete API -- START
let cityAutoComplete;
let addressAutoComplete;

function handleScriptLoad(
  setCityQuery,
  cityId,
  setOfficeAddressQuery,
  addressId
) {
  // Guard: if Google script isn't loaded (or element missing), do nothing
  if (!window.google?.maps?.places?.Autocomplete) return;
  if (!cityId || !addressId) return;

  cityAutoComplete = new window.google.maps.places.Autocomplete(cityId, {
    types: ["(cities)"],
    componentRestrictions: { country: "us" },
  });
  cityAutoComplete.setFields(["address_components", "formatted_address"]);
  cityAutoComplete.addListener("place_changed", () =>
    handleCityPlaceSelect(setCityQuery)
  );

  addressAutoComplete = new window.google.maps.places.Autocomplete(addressId, {
    types: ["address"],
    componentRestrictions: { country: "us" },
  });
  addressAutoComplete.setFields(["address_components", "formatted_address"]);
  addressAutoComplete.addListener("place_changed", () =>
    handleAddressPlaceSelect(setOfficeAddressQuery)
  );
}

async function handleCityPlaceSelect(updateQuery) {
  try {
    const cityObject = cityAutoComplete?.getPlace?.();
    const query = cityObject?.formatted_address || "";
    updateQuery(query);
  } catch {
    // ignore
  }
}

async function handleAddressPlaceSelect(updateQuery) {
  try {
    const addressObject = addressAutoComplete?.getPlace?.();
    const query = addressObject?.formatted_address || "";
    updateQuery(query);
  } catch {
    // ignore
  }
}
// Google Place Autocomplete API -- END

const safeJSONParse = (value, fallback = null) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`localStorage.setItem failed for key="${key}"`, e);
    return false;
  }
};

const safeSetJSON = (key, obj) => {
  try {
    localStorage.setItem(key, JSON.stringify(obj));
    return true;
  } catch (e) {
    console.warn(`localStorage.setItem(JSON) failed for key="${key}"`, e);
    return false;
  }
};

const CandidateForm = (props) => {
  // Profile Picture
  const [logoUrl, setLogoUrl] = useState("");

  // Home City & Office Address
  const [cityQuery, setCityQuery] = useState("");
  const [officeAddressQuery, setOfficeAddressQuery] = useState("");

  useEffect(() => {
    // Guarded autocomplete init (prevents blank screens on devices where google script is blocked)
    handleScriptLoad(
      setCityQuery,
      document.getElementById("city-place-autocomplete"),
      setOfficeAddressQuery,
      document.getElementById("address-place-autocomplete")
    );
  }, []);

  // Office Selection
  const [form] = Form.useForm();
  const [divisionIds, setDivisionIds] = useState({
    incumbent: undefined,
    ballot: undefined,
  });

  // Birthdate
  const dateFormatList = ["MM/DD/YYYY", "MM/DD/YY"];

  // Profile Type
  const [profileType, setProfileType] = useState("");

  // Navigation - Cancel Button
  const history = useHistory();
  const backToAccountTypesOptions = () => {
    history.push(`/profile-start`);
  };

  // loading
  const [loading, setLoading] = useState(false);

  // Read user once safely
  const storedUser = useMemo(
    () => safeJSONParse(localStorage.getItem("user"), {}),
    []
  );

  // Submission
  const onFinish = (values) => {
    if (officeAddressQuery) values.address = officeAddressQuery;
    if (cityQuery) values.homeCity = cityQuery;

    const data = {
      bio: {
        rating: { total: 0, stars: 0 },
        profileImage: logoUrl,
        name: values.name,
        social: {
          website: values.website,
          facebook: values.facebook,
          twitter: values.twitter,
          instagram: values.instagram,
          youtube: values.youtube,
          podcasts: values.podcasts,
        },
        general: { comments: [] },
        office: {
          party: values.politicalParty,
          profileType: values.profileType,
        },
        contact: {
          phone: values.phone,
          fax: values.fax,
          address: values.address,
          email: values.email,
        },
        personal: {
          birthdate: values.birthdate,
          contact: values.contact,
          homeCity: values.homeCity,
          family: {
            spouses: [],
            father: values.father,
            mother: values.mother,
          },
          education: {
            college: values.college,
            highschool: values.highschool,
          },
          religion: values.religion,
          comments: [],
        },
        other: {
          experience: values.otherMembership,
          comments: [],
        },
      },
      positions: [],
      questions: [],
      legislations: [],
      contributions: [],
      committees: [],
      officeSelections: [],
      professionalExperiences: [],
      status: "approve",
      archived: false,
    };

    if (values.positions?.[0]) {
      values.positions.forEach((position) => {
        data.positions.push({
          topic: position.category,
          position: position.detail,
          comments: [],
        });
      });
    }

    if (values.legislations?.[0]) {
      values.legislations.forEach((legislation) => {
        data.legislations.push({
          bill: legislation.bill,
          billName: legislation.billName,
          sponsor: legislation.sponsor,
          summary: legislation.summary,
          vote: legislation.vote,
        });
      });
    }

    if (values.contributions?.[0]) {
      values.contributions.forEach((contribution) => {
        data.contributions.push({
          industry: contribution.industry,
          name: contribution.name,
          amount: contribution.amount,
        });
      });
    }

    if (values.committees?.[0]) {
      values.committees.forEach((committee) => {
        data.committees.push({
          date: committee.committeeDate,
          description: committee.description,
        });
      });
    }

    if (values.officeSelections?.[0]) {
      values.officeSelections.forEach((officeSelection) => {
        data.officeSelections.push({
          politicalDivision: officeSelection.politicalDivision,
          title: officeSelection.title,
          position: officeSelection.position,
          district: officeSelection.district,
          state: officeSelection.state,
          city: officeSelection.division,
          office: officeSelection.office,
          term: officeSelection.term,
        });
      });
    }

    if (values.professionalExperiences?.length) {
      values.professionalExperiences.forEach((professionalExperience) => {
        data.professionalExperiences.push({
          title: professionalExperience.title,
          startDate: professionalExperience.startDate,
          endDate: professionalExperience.endDate,
        });
      });
    }

    if (values.spouses?.[0]) {
      values.spouses.forEach((spouse) => {
        data.bio.personal.family.spouses.push({
          Name: spouse.spouse,
          childrens: spouse.noOfChildren,
        });
      });
    }

    setLoading(true);

    return axios
      .post(`${config.CANDIDATES}/candidates/insert`, JSON.stringify(data), {
        headers: {
          token: localStorage.getItem("access_token"),
        },
      })
      .then((response) => {
        if (response.status !== 200) {
          message.error(
            "Something went wrong, make sure you are connected to the internet."
          );
          return;
        }

        // Use safe parsed user (prevents blank screens)
        const currentUser =
          safeJSONParse(localStorage.getItem("user"), storedUser) || storedUser || {};

        if (currentUser?.role !== "admin") {
          const candidate_id = response?.data?.insertedId;

          const doc = JSON.stringify({
            role: "candidate",
            nickname: values.name,
            picture:
              logoUrl === ""
                ? "https://s3.amazonaws.com/dev.nocap.com/uploads/logo.png"
                : logoUrl,
            candidateId: candidate_id,
          });

          return axios
            .patch(
              `${config.USERS}/users`,
              {},
              {
                headers: {
                  token: localStorage.getItem("access_token"),
                  doc: encodeURIComponent(doc),
                },
              }
            )
            .then(() => {
              safeSetItem("role", "candidate");
              Modal.success({
                content:
                  "Thank you for creating your account with NoCap. Profile is under review, we will get back to you in 72 hrs",
              });
              history.push("/");
            });
        } else {
          Modal.success({
            content: "You have successfully added a candidate.",
          });
          history.push("/");
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Something went wrong submitting the candidate.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Form
      name="candidateForm"
      className="accountForm"
      onFinish={onFinish}
      initialValues={{
        positions: [""],
        legislations: [""],
        contributions: [""],
        committees: [""],
      }}
      layout="vertical"
      form={form}
    >
      <Row gutter={[12]}>
        <Col span={24} style={{ marginBottom: "150px" }}>
          <SingleImageUploader
            handleImageUpload={(url) => setLogoUrl(url)}
            imagePreviewUrl={logoUrl}
            typeKey={"user"}
            title="Upload Profile Image"
            layout={"logo"}
          />
        </Col>

        <Col span={24}>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              {
                required: true,
                message: "Please enter your full name",
              },
            ]}
          >
            <Input
              placeholder="enter full name"
              onPressEnter={(e) => (e.keyCode === 13 ? e.preventDefault() : "")}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label={
              <span>
                Contact Information{" "}
                <span style={{ fontSize: 10, fontStyle: "italic" }}>
                  (not available to the public)
                </span>
              </span>
            }
            name="contact"
            rules={[
              {
                required: true,
                message: "Please enter your contact information",
              },
            ]}
          >
            <InputNumber
              suffix={<PhoneOutlined />}
              placeholder="enter primary number"
              onPressEnter={(e) => (e.keyCode === 13 ? e.preventDefault() : "")}
              controls={false}
              style={{ width: "100%" }}
              addonAfter={<PhoneOutlined />}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12}>
          <Form.Item label="Date of Birth" name="birthdate">
            <DatePicker
              style={{ width: "100%" }}
              placeholder="select a date (mm/dd/yyyy)"
              format={dateFormatList}
              popupStyle={{ color: "black" }}
            />
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            label="Profile type"
            name="profileType"
            rules={[
              {
                required: true,
                message: "Please select your profile type",
              },
            ]}
          >
            <Select
              showSearch
              placeholder="select profile type"
              onChange={(value) => setProfileType(value)}
              options={[
                { value: "new candidate", label: "new candidate" },
                { value: "candidate", label: "incumbent" },
                { value: "elected official", label: "elected official" },
              ]}
            />
          </Form.Item>
        </Col>

        <PoliticalParty />

        <OfficeSelectionReformed
          form={form}
          type="incumbent"
          setDivisionIds={setDivisionIds}
          divisionIds={divisionIds}
        />

        <Col span={24}>
          <OfficeContacts
            setOfficeAddressQuery={setOfficeAddressQuery}
            officeAddressQuery={officeAddressQuery}
          />
        </Col>

        <Position />
        <Col span={24}>
          <Vote />
        </Col>
        <Col span={24}>
          <Contribution />
        </Col>

        <Col xs={{ span: 24 }} lg={{ span: 12 }}>
          <Form.Item label="Residence(s)">
            <Input
              suffix={<EnvironmentOutlined />}
              id="city-place-autocomplete"
              type="text"
              placeholder="search by city and state"
              onChange={(event) => setCityQuery(event.target.value)}
              value={cityQuery}
              onPressEnter={(e) => (e.keyCode === 13 ? e.preventDefault() : "")}
            />
          </Form.Item>
        </Col>

        <Religion />
        <Col span={12} />
        <Col span={24}>
          <FamilyNew />
        </Col>
        <Col span={24}>
          <Education />
        </Col>
        <Col span={24}>
          <ProfessionalExperience />
        </Col>
        <Col span={24}>
          <Committee />
        </Col>
        <Col span={24}>
          <SocialMedia SocialMedia />
        </Col>

        <Col span={24} style={{ textAlign: "right" }}>
          <Form.Item>
            <Button
              className="btn-bordered"
              onClick={backToAccountTypesOptions}
              style={{ margin: "0px 5px", width: "85px" }}
              type="text"
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              htmlType="submit"
              style={{ margin: "0px 5px", width: "85px" }}
              type="text"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default CandidateForm;
